import type { JSONContent } from "@tiptap/core"
import type { TimelineDocument } from "@/lib/timeline"
import type { CanvasMediaKind, CanvasModel, CanvasReferenceItem } from "@/types/canvas"
import {
  flowConnectorHandles,
  flowConnectorMatch,
  flowConnectors,
  flowConnectsWire,
  flowDropTargetKinds,
  flowFreshConnectorNode,
  flowKindTargetPort,
  type FlowConnectorNode,
} from "@/types/flow-connectors"

export type FlowNodeKind =
  | "upload"
  | "library"
  | "image"
  | "video"
  | "video_transition"
  | "audio"
  | "video_upscale"
  | "video_depth"
  | "chain"
  | "shot"
  | "character"
  | "location"
  | "prop"

export const FLOW_IMAGE_SOURCE = "image"
export const FLOW_IMAGE_TARGET = "references"
export const FLOW_VIDEO_PORT = "video"
export const FLOW_ENTITY_PORT = "entity"
export const FLOW_AUDIO_PORT = "audio"
export const FLOW_LINKS_PORT = "links"
export const FLOW_FRAMES_PORT = "frames"

/** Совпадает с FlowNode::FRAME_VIDEO_PROVIDERS. */
export const FLOW_FRAME_VIDEO_PROVIDERS = ["byteplus_seedance_video", "openrouter_video"] as const

/** FlowNode::TITLE_MAX_LENGTH. Имя цепочки совпадает с именем финальной ноды. */
export const FLOW_NODE_TITLE_MAX_LENGTH = 80

export function flowNodeEntityKind(kind: FlowNodeKind): kind is "character" | "location" | "prop" {
  return kind === "character" || kind === "location" || kind === "prop"
}

/** Подпись шота или имя сущности, если нода на них ссылается. */
export function flowNodeSubjectName(node: Pick<FlowNodeRow, "kind" | "shot_label" | "entity_name">): string | null {
  if (node.kind === "shot") {
    const label = node.shot_label?.trim()
    if (label) return label
  }
  if (flowNodeEntityKind(node.kind)) {
    const name = node.entity_name?.trim()
    if (name) return name
  }
  return null
}

export type FlowGenerationStatus = "queued" | "processing" | "completed" | "failed"

export type FlowBoardRow = {
  id: number
  number: number
  title: string
  viewport_x: number
  viewport_y: number
  viewport_zoom: number
}

export type FlowNodeInput = {
  id: number
  source_node_id: number
  source_port?: string
  target_port?: string
  position: number
  slot_index?: number | null
  reference_label?: string | null
  reference_position?: number | null
  media_type?: "image" | "video" | "audio" | null
  keyframe?: boolean
  url?: string | null
  thumb_url?: string | null
  download_url?: string | null
}

export type FlowNodeRow = {
  id: number
  kind: FlowNodeKind
  position_x: number
  position_y: number
  display_width: number | null
  prompt_text: string
  prompt_doc: JSONContent | null
  model_id: string | null
  model_params: Record<string, string>
  use_project_styles: boolean
  generation_status: FlowGenerationStatus | null
  generation_error: string | null
  media_url: string | null
  media_thumb_url: string | null
  download_url: string | null
  content_type: string | null
  aspect_ratio: string | null
  media_width: number | null
  media_height: number | null
  version_count: number
  shot_id: number | null
  shot_label: string | null
  character_id: number | null
  location_id: number | null
  prop_id: number | null
  entity_name: string | null
  title: string | null
  duration_ms: number | null
  editor_timeline: TimelineDocument | null
  /** Кадры filmstrip. У генерации — полоса TimelineStrip (пока её нет — постер); у шота лента из playback. */
  timeline_frame_urls?: string[]
  /** Входящие связи, в порядке подключения. */
  inputs?: FlowNodeInput[]
  /** Ждёт входные ноды в пачке перегенерации. Генерация самой ноды ещё не началась. */
  batch_waiting?: boolean
  /** Черновик референсов image-ноды. У остальных kind пустой список. */
  reference_items?: CanvasReferenceItem[]
  chain_id?: number | null
  chain_title?: string | null
  chain_output_media?: FlowChainMedia | null
  chain_interface_error?: string | null
  chain_slots?: FlowChainSlot[]
  /** Меняется вместе с шагами шаблона, чтобы подпись цены перезапросилась. */
  chain_quote_key?: string | null
  /** На служебной доске: начальная нода отдаётся слотом, её результат при запуске не берётся. */
  chain_input?: boolean
}

export type FlowChainMedia = "image" | "video"

export type FlowChainSlot = {
  position: number
  media: FlowChainMedia
  label: string
}

export type FlowChainRow = {
  id: number
  title: string
  flow_id: number
  output_media: FlowChainMedia | null
  interface_error: string | null
  slots: FlowChainSlot[]
  /** Прямые вложенные цепочки. Клиент по ним прячет циклы в палитре шаблона. */
  uses_chain_ids?: number[]
}

export type FlowChainEditor = {
  id: number
  title: string
  /** Текст плашки. Пусто, если замечание уже видно на нодах или граф ещё не начат. */
  interface_notice: string | null
  /** Финальная нода, пока граф цепочки собран. Её название и есть название цепочки. */
  output_node_id: number | null
  return_flow: string | null
  /** Id цепочек снаружи внутрь: назад открывает последнюю, а не доску. */
  via: string | null
}

export function flowNodePlaysVideo(
  node: Pick<FlowNodeRow, "kind" | "content_type" | "media_url"> & Pick<FlowNodeRow, "chain_output_media">,
): boolean {
  if (!node.media_url) return false
  if (
    node.kind === "shot" ||
    node.kind === "video" ||
    node.kind === "video_transition" ||
    node.kind === "video_upscale" ||
    node.kind === "video_depth"
  ) {
    return true
  }
  if (node.kind === "chain") return node.chain_output_media === "video"
  return Boolean(node.content_type?.startsWith("video/"))
}

export type FlowDefaults = {
  image_model_id: string
  video_model_id: string
  frame_format: string
}

export type FlowModels = CanvasModel[]

function connectorNode(node: Partial<FlowNodeRow> & Pick<FlowNodeRow, "kind">, id = 0): FlowConnectorNode {
  return {
    id: node.id ?? id,
    kind: node.kind,
    content_type: node.content_type,
    inputs: node.inputs,
    chain_id: node.chain_id,
    chain_interface_error: node.chain_interface_error,
    chain_slots: node.chain_slots,
    chain_output_media: node.chain_output_media,
  }
}

export function flowNodeImageOutput(node: Pick<FlowNodeRow, "kind" | "content_type" | "chain_output_media">): boolean {
  return flowConnectsWire(connectorNode(node), "source", "image")
}

export function flowNodeVideoOutput(node: Pick<FlowNodeRow, "kind" | "content_type" | "chain_output_media">): boolean {
  return flowConnectsWire(connectorNode(node), "source", "video")
}

export function flowNodeAcceptsVideo(
  node: Pick<FlowNodeRow, "kind" | "chain_id" | "chain_interface_error" | "chain_slots">,
): boolean {
  return flowConnectsWire(connectorNode(node), "target", "video")
}

/** Вход `references`: картинка кормит image-ноду так же, как видео. Слоты кадров — не референсы. */
export function flowNodeAcceptsImageReferences(
  node: Pick<FlowNodeRow, "kind" | "chain_id" | "chain_interface_error" | "chain_slots">,
): boolean {
  return flowConnectors(connectorNode(node)).some(
    (connector) =>
      connector.side === "target" && connector.port === FLOW_IMAGE_TARGET && connector.types.includes("image"),
  )
}

export function flowNodeAudioOutput(node: Pick<FlowNodeRow, "kind" | "content_type">): boolean {
  return flowConnectsWire(connectorNode(node), "source", "audio")
}

export function flowNodeShotVideoReference(
  node: Pick<FlowNodeRow, "kind" | "content_type" | "chain_output_media">,
): boolean {
  return flowConnectsWire(connectorNode(node), "source", "shot_video")
}

export function mergeFlowNodes(prev: readonly FlowNodeRow[], incoming: readonly FlowNodeRow[]): FlowNodeRow[] {
  if (incoming.length === 0) return [...prev]
  const byId = new Map(incoming.map((node) => [node.id, node]))
  const seen = new Set<number>()
  const next = prev.map((node) => {
    const updated = byId.get(node.id)
    if (!updated) return node
    seen.add(node.id)
    return updated
  })
  for (const node of incoming) {
    if (!seen.has(node.id)) next.push(node)
  }
  return next
}

export function flowConnectionAllowed(
  rows: readonly FlowNodeRow[],
  connection: {
    source: string | null
    target: string | null
    sourceHandle?: string | null
    targetHandle?: string | null
  },
): boolean {
  if (!connection.source || !connection.target || connection.source === connection.target) return false
  const source = rows.find((row) => String(row.id) === connection.source)
  const target = rows.find((row) => String(row.id) === connection.target)
  if (!source || !target) return false
  return (
    flowConnectorMatch(
      connectorNode(source),
      connection.sourceHandle ?? null,
      connectorNode(target),
      connection.targetHandle ?? null,
    ) != null
  )
}

export type FlowDropTargetKind = "image" | "video" | "video_transition" | "video_upscale" | "video_depth" | "shot"

export type FlowDropTarget = {
  kind: FlowDropTargetKind
  targetPort: string
}

/** Ручка, к которой React Flow сажает ребро. У видео-ноды клип и картинки делят references. */
export function flowVisualTargetHandle(
  targetKind: FlowNodeKind,
  targetPort: string | null | undefined,
  target?: Partial<FlowNodeRow> & Pick<FlowNodeRow, "kind">,
): string {
  const port = targetPort ?? FLOW_IMAGE_TARGET
  const node = target ? connectorNode(target) : flowFreshConnectorNode(targetKind)
  const connector = flowConnectors(node).find((item) => item.side === "target" && item.port === port)
  return connector?.handle ?? port
}

function sourceNodeForHandle(handle: string): FlowConnectorNode {
  if (handle === FLOW_VIDEO_PORT) return flowFreshConnectorNode("video", -3)
  if (handle === FLOW_ENTITY_PORT) return flowFreshConnectorNode("character", -3)
  if (handle === FLOW_AUDIO_PORT) return flowFreshConnectorNode("audio", -3)
  return flowFreshConnectorNode("image", -3)
}

/** Порты, которые пишем в ребро. Id общей ручки не становится портом клипа. */
export function flowConnectPorts(
  targetKind: FlowNodeKind,
  sourceHandle: string | null | undefined,
  targetHandle: string | null | undefined,
): { sourcePort: string; targetPort: string }
export function flowConnectPorts(
  targetKind: FlowNodeKind,
  sourceHandle: string | null | undefined,
  targetHandle: string | null | undefined,
  live: Partial<FlowNodeRow> & Pick<FlowNodeRow, "kind">,
): { sourcePort: string; targetPort: string } | null
export function flowConnectPorts(
  targetKind: FlowNodeKind,
  sourceHandle: string | null | undefined,
  targetHandle: string | null | undefined,
  live?: Partial<FlowNodeRow> & Pick<FlowNodeRow, "kind">,
): { sourcePort: string; targetPort: string } | null {
  const sourcePort = sourceHandle ?? FLOW_IMAGE_SOURCE
  const target = live ? connectorNode(live) : flowFreshConnectorNode(targetKind, -4)
  const match = flowConnectorMatch(sourceNodeForHandle(sourcePort), sourcePort, target, targetHandle ?? null)
  if (match) return { sourcePort: match.source.port, targetPort: match.target.port }
  // Без живой ноды ручка и есть порт. С живой — занятая общая ручка не пишется как порт.
  if (live) return null
  return { sourcePort, targetPort: targetHandle ?? FLOW_IMAGE_TARGET }
}

/** Вход новой ноды для выхода, который отпустили на пустой доске. */
export function flowDropTargetPort(kind: FlowDropTargetKind): string {
  return flowKindTargetPort(kind)
}

/**
 * Ноды, которые можно создать, отпустив выход `sourcePort` на пустой доске.
 * Порядок — порядок пунктов меню. Те же правила, что у `flowConnectionAllowed`.
 */
export function flowDropTargets(
  node: Pick<FlowNodeRow, "kind" | "content_type" | "chain_output_media">,
  sourcePort: string,
): FlowDropTarget[] {
  const origin = connectorNode(node, -1)
  const targets: FlowDropTarget[] = []
  for (const kind of flowDropTargetKinds()) {
    const fresh = flowFreshConnectorNode(kind, -2)
    for (const handle of flowConnectorHandles(fresh, "target")) {
      const match = flowConnectorMatch(origin, sourcePort, fresh, handle)
      if (!match) continue
      targets.push({ kind, targetPort: match.target.port })
      break
    }
  }
  return targets
}

export type FlowConnectMenuKind = FlowDropTargetKind | "character" | "location" | "prop" | "audio"

export type FlowDropSource = {
  kind: FlowConnectMenuKind
  sourcePort: string
}

const FLOW_DROP_SOURCE_ORDER: readonly FlowDropSource[] = [
  { kind: "image", sourcePort: FLOW_IMAGE_SOURCE },
  { kind: "video", sourcePort: FLOW_VIDEO_PORT },
  { kind: "video_upscale", sourcePort: FLOW_VIDEO_PORT },
  { kind: "video_depth", sourcePort: FLOW_VIDEO_PORT },
  { kind: "shot", sourcePort: FLOW_VIDEO_PORT },
  { kind: "character", sourcePort: FLOW_ENTITY_PORT },
  { kind: "location", sourcePort: FLOW_ENTITY_PORT },
  { kind: "prop", sourcePort: FLOW_ENTITY_PORT },
  { kind: "audio", sourcePort: FLOW_AUDIO_PORT },
]

/**
 * Ноды, которые можно создать, отпустив вход `targetPort` на пустой доске.
 * Порядок — порядок пунктов меню. Те же правила, что у `flowConnectionAllowed`,
 * для только что созданной ноды без файла и без цепочки.
 */
export function flowDropSources(node: FlowNodeRow, targetPort: string): FlowDropSource[] {
  return FLOW_DROP_SOURCE_ORDER.filter((candidate) => {
    const source = freshDropSource(candidate)
    return flowConnectionAllowed([source, node], {
      source: String(source.id),
      target: String(node.id),
      sourceHandle: candidate.sourcePort,
      targetHandle: targetPort,
    })
  })
}

/** Ручки, которые карточка реально рисует. Общая references у видео прячет отдельный video-вход. */
export function flowNodeTargetHandles(node: Partial<FlowNodeRow> & Pick<FlowNodeRow, "kind">): string[] {
  return flowConnectorHandles(connectorNode(node), "target")
}

export function flowNodeSourceHandles(node: Partial<FlowNodeRow> & Pick<FlowNodeRow, "kind">): string[] {
  return flowConnectorHandles(connectorNode(node), "source")
}

export type FlowDragHandle = {
  id: number
  handleType: "source" | "target"
  handleId: string
}

export type FlowResolvedDrop = {
  sourceId: number
  targetId: number
  sourceHandle: string
  targetHandle: string
}

/** Куда садится связь, если её отпустили на карточку, а не на ручку. */
export function flowResolveNodeDrop(
  rows: readonly FlowNodeRow[],
  from: FlowDragHandle,
  hitId: number,
): FlowResolvedDrop | null {
  if (from.id === hitId) return null
  const origin = rows.find((row) => row.id === from.id)
  const hit = rows.find((row) => row.id === hitId)
  if (!origin || !hit) return null
  if (from.handleType === "source") {
    for (const targetHandle of flowNodeTargetHandles(hit)) {
      if (
        flowConnectionAllowed(rows, {
          source: String(from.id),
          target: String(hitId),
          sourceHandle: from.handleId,
          targetHandle,
        })
      ) {
        return { sourceId: from.id, targetId: hitId, sourceHandle: from.handleId, targetHandle }
      }
    }
    return null
  }
  for (const sourceHandle of flowNodeSourceHandles(hit)) {
    if (
      flowConnectionAllowed(rows, {
        source: String(hitId),
        target: String(from.id),
        sourceHandle,
        targetHandle: from.handleId,
      })
    ) {
      return { sourceId: hitId, targetId: from.id, sourceHandle, targetHandle: from.handleId }
    }
  }
  return null
}

function freshDropSource(candidate: FlowDropSource): FlowNodeRow {
  return flowFreshConnectorNode(candidate.kind, -1) as unknown as FlowNodeRow
}

export function flowNodeGenerates(kind: FlowNodeKind): boolean {
  return kind === "image" || kind === "video" || kind === "video_transition"
}

/** media_kind холста. Видео переход пишет video, не свой kind. */
export function flowNodeCanvasMediaKind(kind: FlowNodeKind): CanvasMediaKind | null {
  if (kind === "image") return "image"
  if (kind === "video" || kind === "video_transition") return "video"
  return null
}

export function flowFrameVideoModels(models: readonly CanvasModel[]): CanvasModel[] {
  return models.filter(
    (model) =>
      model.media_kind === "video" &&
      model.enabled &&
      !model.restricted &&
      FLOW_FRAME_VIDEO_PROVIDERS.includes(model.provider as (typeof FLOW_FRAME_VIDEO_PROVIDERS)[number]),
  )
}

/** Модели панели генерации. Переход берёт только провайдеров с первым и последним кадром. */
export function flowModelsForNode(kind: FlowNodeKind, models: readonly CanvasModel[]): CanvasModel[] {
  if (kind === "video_transition") return flowFrameVideoModels(models)
  return models.filter((model) => model.media_kind === kind && model.enabled && !model.restricted)
}

/** Название живёт на ноде. Шот и сущности берут подпись из связанной записи. */
export function flowNodeCustomTitle(kind: FlowNodeKind): boolean {
  return kind !== "shot" && !flowNodeEntityKind(kind)
}

export function flowNodePending(node: FlowNodeRow): boolean {
  return node.generation_status === "queued" || node.generation_status === "processing"
}

const FLOW_BULK_KINDS = new Set<FlowNodeKind>([
  "image",
  "video",
  "video_transition",
  "video_upscale",
  "video_depth",
  "chain",
  "shot",
])

/** Нода может войти в пачку: умеет запускаться и ещё не стоит в очереди. */
export function flowNodeCanBulkRegenerate(node: FlowNodeRow): boolean {
  return FLOW_BULK_KINDS.has(node.kind) && !flowNodePending(node) && node.batch_waiting !== true
}

/** Что доска делает один раз при открытии: вписывает все ноды или сначала упорядочивает их. */
export type FlowOpenLayout = "fit" | "arrange"
